<template lang="pug">
.remind-modal
  b-modal(v-model="_value" size="xl" no-close-on-backdrop no-close-on-esc hide-header @ok="ok")
    .messages
      div 本日、以下の面会予約がありますので、ご確認ください。

    .remind-modal-table
      .thead
        .header-line
          .th.type 種別
          .th.status 状態
          .th.date 面会日時
          .th.location(v-if="_showLocation") 面会場所
          .th.room
            .d-flex.flex-column
              span 利用者名
              span 部屋名
          .th.visitor
            .d-flex.flex-column
              span 面会者名
              span 続柄
          .th.tel 電話番号
          .th.text(v-if="_showText") 家族からの連絡事項
      .tbody
        .body-line(v-for="reservation in _reservations" :class="{ 'deleted-reservation': reservation.deleteFlag }")
          .td.type.center(v-if="reservation.type == 0")
            .icon-offline(title="訪問面会")
          .td.type.center(v-else-if="reservation.type == 1")
            .icon-online(title="オンライン面会")

          .td.status.center(v-if="reservation.deleteFlag == true") キャンセル済
          .td.status.center.status-no-consented(v-else-if="reservation.consented == false") 未承諾
          .td.status.center.status-consented(v-else) 承諾済

          .td.date.center
            span {{ getDate(reservation.datetime) }}
            span （
            span(:class="weekCssClass(reservation.datetime)") {{ getWeek(reservation.datetime) }}
            span ）
            span {{ getTime(reservation.datetime) }}
          .td.location(v-if="_showLocation")
            .d-flex.flex-row(v-if="reservation.type == 0")
              .text-content.flex-fill
                span(:id="`location-${reservation.id}`") {{ reservation.location }}
                b-popover(:target="`location-${reservation.id}`" triggers="hover" :placement="'bottomright'")
                  span(style="white-space: pre-wrap; word-wrap: break-word;") {{ reservation.location }}
          .td.room
            .d-flex.flex-column
              span {{ reservation.residentName }}
              span {{ reservation.residentRoom }}
          .td.visitor
            .d-flex.flex-column
              span {{ reservation.familyName }}
              span {{ reservation.familyRelation }}
          .td.tel {{ reservation.familyTel }}
          .td.text(v-if="_showText") {{ reservation.text }}
      
    .loading(v-show="isLoading")
      .loading-msg
        b-spinner.spinner
        span.message.align-self-center 読込み中...

    template(#modal-footer="{ ok }")
      .d-flex.flex-row.justify-content-end.align-items-center(style="width:100%;")
        .flex-fill.mr-2
          b-form-checkbox(v-model="showCanceled") キャンセル済を表示
        b-button.btn.modal-ok-btn(@click="print") 印刷
        b-button.btn.modal-ok-btn(@click="ok") 閉じる
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { ReservationsGetModel } from "@/commons/services/Reservations";
import { ReservationSettingsGetModel } from "@/commons/services/ReservationSettings";
import { OnlineReservationSettingsGetModel } from "@/commons/services/OnlineReservationSettings";

/**
 * ### 面会予約のリマインドモーダル
 */
@Component
export default class RemindReservatoinModal extends Vue {
  /** 予約 */
  @Prop({
    required: true
  })
  value!: boolean;
  /** 予約 */
  @Prop({ required: true })
  reservations!: Array<ReservationsGetModel>;
  /** ローディング表示可否 */
  @Prop({
    required: true
  })
  isLoading!: boolean;

  @Prop()
  visitSetting!: ReservationSettingsGetModel;

  @Prop()
  onlineSetting!: OnlineReservationSettingsGetModel;

  /** 削除済みを表示するか否か */
  showCanceled: boolean = false;

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  get _reservations(): Array<ReservationsGetModel> {
    return this.reservations.filter((reservation) => {
      // 削除済を表示する場合、全レコードを表示させる
      if (this.showCanceled == true) {
        return true;
      }
      // 削除済を表示しない場合、削除されていないレコードのみを表示させる
      else {
        if (reservation.deleteFlag == false) {
          return true;
        } else {
          return false;
        }
      }
    });
  }

  get _showLocation(): boolean {
    // 「訪問面会を使用している」かつ「訪問面会の面会場所」を使用している場合は、面会場所を追加
    if (userStore.facility.permittedFunctions.reservation == true && this.visitSetting && this.visitSetting.useLocation == true) {
      return true;
    } else {
      return false;
    }
  }

  get _showText(): boolean {
    if (
      (userStore.facility.permittedFunctions.reservation == true && this.visitSetting && this.visitSetting.useText == true) ||
      (userStore.facility.permittedFunctions.onlineReservation == true && this.onlineSetting && this.onlineSetting.useText == true)
    ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "mm月dd日");
  }

  /**
   * 曜日を返す
   */
  public getWeek(date: number): string {
    const dateObject: Date = this.$dateUtil.convertNumberToDate(date);
    return this.$dateUtil.getWeekName(dateObject, "ja");
  }

  /**
   * 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(date: number): string {
    let weekClassName: string = "";
    const weekName: string = this.getWeek(date);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * 時分を返す
   */
  public getTime(date: number): string {
    return this.$dateUtil.convertNumberToString(date, "hh:MM");
  }

  public ok(): void {
    this.$emit("ok");
    return;
  }

  public async print(): Promise<void> {
    this.$emit("print", this.showCanceled);
    return;
  }
}
</script>
